import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { UiButton } from '@/components/ui/Button';
import { UiTypography } from '@/components/ui/Typography';
import { S3_BUCKET_URL } from '@/constants';
import { amplitudeModule } from '@/modules/amplitude';
import { webCampaign } from '@/modules/webCampaign';
import {
  OnboardingOptionItem,
  OnboardingOptionsList,
} from '@/pages/Onboarding/components/List';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { useOnboarding } from '@/pages/Onboarding/context';
import { useManLayoutSelections } from '@/pages/Onboarding/hooks/useManLayoutSelections';
import { OwnAiTitle } from '@/pages/Onboarding/Layouts/OwnAI/components/Title';
import { MAJOR_TURN_OFF_OPTIONS } from '@/pages/Onboarding/options/MajorTurnOff/MAJOR_TURN_OFF_OPTIONS';
import { OnbOptionType } from '@/pages/Onboarding/types';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';
import { preloadImage } from '@/utils/preloadImage';

import {
  MajorTurnOffModalStyles,
  MajorTurnOffStyles,
} from './MajorTurnOff.styles';

import type { LayoutDataType } from '../../types';

export const ScreenMajorTurnOff: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'MajorTurnOff',
      });
    }
  }, []);

  const { ageGeneration, bodyShape, ethnicity } = useManLayoutSelections();

  React.useEffect(() => {
    if (webCampaign.setup.paywallNotes?.includes('real-man-photos')) {
      preloadImage(
        `${S3_BUCKET_URL}/paywall/fast-pay-tab-man/real-man-photos/${ethnicity}_${ageGeneration}_${bodyShape}.png`,
      );
    } else {
      preloadImage(
        `${S3_BUCKET_URL}/paywall/fast-pay-tab-man/${ageGeneration}/${ethnicity}_${bodyShape}.png`,
      );
    }
  }, [ageGeneration, bodyShape, ethnicity]);

  const { wizard } = useOnboarding();

  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const form = useFormContext<LayoutDataType>();
  const major_turn_off = form.watch('major_turn_off');

  const handleChange = (
    option: OnbOptionType<LayoutDataType['major_turn_off']>,
  ) => {
    userService.createOrUpdate({
      onboarding_data: {
        major_turn_off: option.title,
      },
    });
    setIsModalOpen(true);
  };

  const handleModalOption = (option: boolean): void => {
    userService.createOrUpdate({
      onboarding_data: {
        send_photos: option,
      },
    });

    // Пин для AI Men делается на экране Identify (по полю identify)
    setIsModalOpen(false);
    nextStepTimeout(wizard.nextStep);
  };

  const getSendPhotosModalImage = () => {
    return `${S3_BUCKET_URL}/layout-own-ai-man/modal_send_photos.png`;
  };

  return (
    <MajorTurnOffStyles>
      <OnboardingNavigation />

      <IonContent>
        <OwnAiTitle>What is the major turn-off for you?</OwnAiTitle>
        <OnboardingOptionsList>
          {MAJOR_TURN_OFF_OPTIONS.map((option) => (
            <OnboardingOptionItem
              checked={major_turn_off === option.id}
              control={form.control}
              option={option}
              name="major_turn_off"
              key={option.id}
              onChange={() => handleChange(option)}
              TEST_ID={`major_turn_off-${option.id}`}
            />
          ))}
        </OnboardingOptionsList>
      </IonContent>

      <MajorTurnOffModalStyles
        id="major_turn_off"
        isOpen={isModalOpen}
        onDidDismiss={() => {
          setIsModalOpen(false);
        }}
      >
        <div className="modal-wrapper">
          <img className="major-turn-off-img" src={getSendPhotosModalImage()} />

          <UiTypography variant="headline-small" align="center">
            Do you want him to send photos?
          </UiTypography>

          <div className="buttons">
            <UiButton
              size="large"
              color="primary"
              round="sm"
              onClick={() => handleModalOption(true)}
            >
              Yes
            </UiButton>
            <UiButton
              size="large"
              color="primary"
              round="sm"
              onClick={() => handleModalOption(false)}
            >
              No
            </UiButton>
          </div>
        </div>
      </MajorTurnOffModalStyles>
    </MajorTurnOffStyles>
  );
};
